/**
 * Database Routes Panel Component
 * 
 * Right panel displaying routes from UAT/Production database.
 * Automatically fetches and displays routes when prerequisites are met.
 * Allows users to view, search, and select routes for download/delete operations.
 * 
 * @module DatabaseRoutesPanel
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  Button,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  fetchAllRoutes,
  fetchRouteStopList,
  fetchNetworkSettings,
  type UATNetworkSettingsResponse,
  type Environment,
} from '../../../../services/fixedRouteUatClient';
import { RouteCard } from './RouteCard';
import { RouteSearchBar } from './RouteSearchBar';
import { useDebounce } from '../../../../hooks/useDebounce';
import type { UATRoute } from '../../../../services/fixedRouteUatClient';
import { useTranslation } from 'react-i18next';

/**
 * Simplified route format from fetchRoutesForComparison
 */
interface SimplifiedRoute {
  id: string;
  name: string;
  version?: string;
  stopCount: number;
  lastModified?: string;
  stops: Array<{
    id: string;
    order: number;
    timeToNextStop: number;
    timeSpentAtStop: number;
  }>;
}

/**
 * Convert simplified route to UATRoute format for RouteCard
 * Includes fetched stops if available
 */
function convertToUATRoute(route: SimplifiedRoute, fetchedStops?: Array<{
  id: string;
  name?: string;
  label?: string;
  coordinates?: { lat: number; lng: number };
  order: number;
  timeToNextStop?: number;
  timeSpentAtStop?: number;
}>): UATRoute {
  // Use fetched stops if available, otherwise use basic stop info
  const stops = fetchedStops || route.stops.map((stop) => ({
    id: stop.id,
    order: stop.order,
    timeToNextStop: stop.timeToNextStop,
    timeSpentAtStop: stop.timeSpentAtStop,
  }));
  
  return {
    id: route.id,
    label: route.name,
    version: route.version,
    stops: stops.map((stop) => ({
      id: stop.id,
      order: stop.order,
      timeToNextStop: stop.timeToNextStop ?? 0,
      timeSpentAtStop: stop.timeSpentAtStop ?? 0,
    })),
    routeStops: {
      next: stops.map((stop) => {
        const result: {
          id: string;
          name?: string;
          coordinates?: { lat: number; lng: number };
          order?: number;
        } = {
          id: stop.id,
          order: stop.order,
        };
        // Only include optional properties if they exist
        if ('name' in stop && stop.name !== undefined) {
          result.name = stop.name;
        }
        if ('coordinates' in stop && stop.coordinates !== undefined) {
          result.coordinates = stop.coordinates;
        }
        return result;
      }),
    },
  };
}

/**
 * Props for DatabaseRoutesPanel component
 */
export interface DatabaseRoutesPanelProps {
  /** JWT token for API authentication */
  jwtToken: string;
  /** Selected environment (UAT or Production) */
  environment: Environment;
  /** Service date for route operations */
  serviceDate: string;
  /** Timezone for route operations */
  timezone: string;
  /** Selected product ID */
  selectedProductId: string;
  /** Selected network ID */
  selectedNetworkId: number | null;
  /** Network settings response (for timezone) */
  networkSettings: UATNetworkSettingsResponse | null;
  /** Whether download acknowledgment is checked */
  downloadAcknowledged: boolean;
  /** Selected route IDs */
  selectedRouteIds: Set<string>;
  /** Callback when selection changes */
  onSelectionChange: (selectedIds: Set<string>) => void;
  /** Callback to update route count in parent header */
  onRouteCountChange?: (count: number, filteredCount?: number) => void;
  /** Callback to trigger refresh (called after upload/delete) */
  onRefresh?: () => void;
  /** Callback to register refresh function */
  onRefreshRegister?: (refreshFn: () => void) => void;
  /** Callback when routes are loaded/updated */
  onRoutesChange?: (routes: UATRoute[]) => void;
}

/**
 * DatabaseRoutesPanel Component
 * 
 * Displays routes from database with automatic fetching, search, and selection functionality.
 */
export const DatabaseRoutesPanel: React.FC<DatabaseRoutesPanelProps> = ({
  jwtToken,
  environment,
  serviceDate,
  timezone,
  selectedProductId,
  selectedNetworkId,
  networkSettings,
  downloadAcknowledged,
  selectedRouteIds,
  onSelectionChange,
  onRouteCountChange,
  onRefresh,
  onRefreshRegister,
  onRoutesChange,
}) => {
  const { t } = useTranslation('mapTransfer');
  const [routes, setRoutes] = useState<SimplifiedRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  // Track expanded routes and their fetched stops
  const [expandedRouteIds, setExpandedRouteIds] = useState<Set<string>>(new Set());
  const [routeStopsCache, setRouteStopsCache] = useState<Map<string, Array<{
    id: string;
    name?: string;
    label?: string;
    coordinates?: { lat: number; lng: number };
    order: number;
    timeToNextStop?: number;
    timeSpentAtStop?: number;
  }>>>(new Map());

  // Calculate effective timezone from network settings
  const effectiveTimezone = useMemo(() => {
    return networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
  }, [networkSettings, timezone]);

  // Fetch routes function
  const fetchRoutes = useCallback(async () => {
    if (
      !jwtToken.trim() ||
      !selectedProductId ||
      !downloadAcknowledged
    ) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use fetchAllRoutes to get all routes regardless of service date
      // This ensures routes without service dates are also displayed
      const fetchedRoutes = await fetchAllRoutes(
        jwtToken,
        selectedProductId,
        environment
      );
      setRoutes(fetchedRoutes);

      // NOTE: we deliberately do NOT pre-fetch stops for every route here.
      // The route-list endpoint doesn't include stops, so doing so would mean
      // one getroutestoplist call per route (dozens of slow proxy calls on
      // every load/refresh) just to show a count most users never look at.
      // Stops — and the stop count — are fetched lazily when a card is expanded
      // (see `fetchStopsForRoute`). The card shows the count once it's known.
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('routes.panel.loadFailedFallback');
      setError(errorMessage);
      console.error('[DatabaseRoutesPanel] Error fetching routes:', err);
    } finally {
      setLoading(false);
    }
  }, [
    jwtToken,
    selectedProductId,
    environment,
    downloadAcknowledged,
    t,
  ]);

  // Auto-fetch routes when prerequisites are met
  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Register refresh function with parent
  useEffect(() => {
    if (onRefreshRegister) {
      onRefreshRegister(fetchRoutes);
    }
  }, [onRefreshRegister, fetchRoutes]);

  // Clear selection when routes are refreshed or prerequisites change
  useEffect(() => {
    onSelectionChange(new Set());
  }, [
    selectedProductId,
    environment,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // Sort routes alphabetically by name
  const sortedRoutes = useMemo(() => {
    return [...routes].sort((a, b) => a.name.localeCompare(b.name));
  }, [routes]);

  // Filter routes based on search query
  const filteredRoutes = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return sortedRoutes;
    }

    const query = debouncedSearchQuery.toLowerCase();
    return sortedRoutes.filter((route) => {
      const nameMatch = route.name.toLowerCase().includes(query);
      const idMatch = route.id.toLowerCase().includes(query);
      const versionMatch = route.version?.toLowerCase().includes(query);
      return nameMatch || idMatch || versionMatch;
    });
  }, [sortedRoutes, debouncedSearchQuery]);

  // Update parent with route counts
  useEffect(() => {
    if (onRouteCountChange) {
      onRouteCountChange(
        sortedRoutes.length,
        debouncedSearchQuery.trim() ? filteredRoutes.length : undefined
      );
    }
  }, [sortedRoutes.length, filteredRoutes.length, debouncedSearchQuery, onRouteCountChange]);

  // Handle individual route selection
  const handleRouteSelect = (routeId: string, selected: boolean) => {
    const newSelection = new Set(selectedRouteIds);
    if (selected) {
      newSelection.add(routeId);
    } else {
      newSelection.delete(routeId);
    }
    onSelectionChange(newSelection);
  };

  // Handle select all
  const handleSelectAll = () => {
    const allFilteredIds = new Set(filteredRoutes.map((r) => r.id));
    onSelectionChange(allFilteredIds);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    onSelectionChange(new Set());
  };

  const selectedCount = selectedRouteIds.size;
  const allFilteredSelected =
    filteredRoutes.length > 0 &&
    filteredRoutes.every((route) => selectedRouteIds.has(route.id));

  // Fetch stops for a route when it's expanded
  const fetchStopsForRoute = useCallback(async (routeId: string) => {
    if (
      !jwtToken.trim() ||
      !selectedProductId ||
      routeStopsCache.has(routeId)
    ) {
      return;
    }

    try {
      const stopListResponse = await fetchRouteStopList(
        jwtToken,
        selectedProductId,
        routeId,
        0,
        1000,
        'order',
        'ASC',
        environment
      );

      if (stopListResponse.data?.entries) {
        const stops = stopListResponse.data.entries.map((entry) => ({
          id: entry.id,
          name: entry.label, // API uses 'label' as the stop name
          label: entry.label,
          coordinates: {
            lat: entry.latitude,
            lng: entry.longitude,
          },
          order: entry.order ?? 0,
          // Note: getroutestoplist doesn't return timeToNextStop or timeSpentAtStop
          timeToNextStop: undefined,
          timeSpentAtStop: undefined,
        }));
        
        setRouteStopsCache((prev) => {
          const next = new Map(prev);
          next.set(routeId, stops);
          return next;
        });
        
        // Update the route's stop count in the routes state
        setRoutes((prevRoutes) => {
          return prevRoutes.map((route) => {
            if (route.id === routeId) {
              return {
                ...route,
                stopCount: stops.length,
              };
            }
            return route;
          });
        });
      }
    } catch (err) {
      console.error(`[DatabaseRoutesPanel] Failed to fetch stops for route ${routeId}:`, err);
    }
  }, [jwtToken, selectedProductId, environment, routeStopsCache]);

  // Fetch stops when a route is expanded
  useEffect(() => {
    expandedRouteIds.forEach((routeId) => {
      if (!routeStopsCache.has(routeId)) {
        fetchStopsForRoute(routeId);
      }
    });
  }, [expandedRouteIds, routeStopsCache, fetchStopsForRoute]);

  // Convert routes to UATRoute format for RouteCard
  const uatRoutes = useMemo(() => {
    return filteredRoutes.map((route) => {
      const fetchedStops = routeStopsCache.get(route.id);
      return convertToUATRoute(route, fetchedStops);
    });
  }, [filteredRoutes, routeStopsCache]);

  // Notify parent when routes change
  useEffect(() => {
    if (onRoutesChange) {
      const uatRoutes = filteredRoutes.map((route) => convertToUATRoute(route));
      onRoutesChange(uatRoutes);
    }
  }, [onRoutesChange, filteredRoutes]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      {/* Search Bar */}
      <Box sx={{ padding: 2, paddingBottom: 1 }}>
        <RouteSearchBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          placeholder={t('routes.panel.searchPlaceholderDatabase')}
        />

        {/* Selection Controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allFilteredSelected}
                  indeterminate={selectedCount > 0 && !allFilteredSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleSelectAll();
                    } else {
                      handleDeselectAll();
                    }
                  }}
                  size="small"
                  disabled={loading || error !== null || filteredRoutes.length === 0}
                />
              }
              label={
                <Typography variant="body2">
                  {t('routes.panel.selectAllLabel', { count: filteredRoutes.length })}
                </Typography>
              }
            />
          </Box>

          {selectedCount > 0 && (
            <Typography variant="body2" color="primary">
              {t('routes.panel.selectedLabel', { count: selectedCount })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Box sx={{ paddingX: 2, paddingBottom: 1 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchRoutes}>
                {t('common.retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Route List */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // Critical for flex scrolling
          overflow: 'auto',
          paddingX: 2,
          paddingBottom: 2,
        }}
      >
        {loading ? (
          // Loading state with skeletons
          <Box>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ marginBottom: 1 }}>
                <Skeleton variant="rectangular" height={120} />
              </Box>
            ))}
          </Box>
        ) : error ? (
          // Error state (already shown above, but show empty list)
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              padding: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('routes.panel.connectionError')}
            </Typography>
          </Box>
        ) : uatRoutes.length === 0 ? (
          // Empty state
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              padding: 4,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchQuery.trim()
                ? t('routes.panel.noMatch')
                : t('routes.panel.emptyDatabase')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery.trim()
                ? t('routes.panel.noMatchHint')
                : t('routes.panel.emptyDatabaseHint')}
            </Typography>
          </Box>
        ) : (
          // Route cards
          uatRoutes.map((route) => (
            <RouteCard
              key={route.id}
              route={route}
              source="database"
              selected={selectedRouteIds.has(route.id)}
              onSelect={(selected) => handleRouteSelect(route.id, selected)}
              expanded={expandedRouteIds.has(route.id)}
              onExpand={(expanded) => {
                setExpandedRouteIds((prev) => {
                  const next = new Set(prev);
                  if (expanded) {
                    next.add(route.id);
                  } else {
                    next.delete(route.id);
                  }
                  return next;
                });
              }}
            />
          ))
        )}
      </Box>
    </Box>
  );
};
